import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'

import SortableTable from '../CommonComponents/SortableTableComponent'
import TableCell from '../CommonComponents/TableCell'
import DupSTModal from './DupSTModal'

import {
  capitalizeFirstLetter,
  tableSorter,
  calcDerivedMetrics,
  getExportValueForColumn,
} from '../../services/helper'

import { bulkSTColumnList } from '../../utils/defaultValues'

const initialColumns = [
  { key: 'search', name: 'Search Term', className: 'col-search-term' },
]

const DupSTTable = ({ onChangeDate }) => {
  const currencyRate = useSelector(state => state.header.currencyRate)
  const currencySign = useSelector(state => state.header.currencySign)
  const campaignTableColumns = useSelector(state => state.pageGlobal.campaignTableColumns)
  const filterValues = useSelector(state => state.pageGlobal.filterValues)
  const campaignsWithHistory = useSelector(state => state.campaign.campaignsWithHistory)
  const findDupsData = useSelector(state => state.bulkEngine.findDupsData)

  const [searchTerms, setSearchTerms] = useState([])
  const [selectedSearchTerm, setSelectedSearchTerm] = useState(null)

  useEffect(() => {
    const campaignsById = {}
    campaignsWithHistory.forEach((campaign) => {
      campaignsById[campaign.campaign_id] = {
        name: campaign.campaign,
        type: campaign.campaignType,
        targetingType: campaign.targeting_type,
        costType: campaign.cost_type,
      }
    })

    const extendedSearchTerms = [];
    Object.keys(findDupsData.searchTerms || {}).forEach((searchTerm) => {
      const total = {
        revenue: 0,
        cost: 0,
        impressions: 0,
        clicks: 0,
        orders: 0,
        st_impr_rank: 0,
        st_impr_share: 0,
        viewable_impressions: 0,
      }

      const children = [];
      findDupsData.searchTerms[searchTerm].forEach((record) => {
        const campaignDetail = campaignsById[record.campaign_id] || {}

        const campaignType = campaignDetail.type || ''

        // Filter by ad type.
        if (filterValues && filterValues['dupResult'] && filterValues['dupResult']['campaignType']) {
          if (campaignType.toLowerCase() !== filterValues['dupResult']['campaignType'].value) {
            return
          }
        }

        total.revenue += parseFloat(record.revenue || 0)
        total.cost += parseFloat(record.cost || 0)
        total.impressions += parseInt(record.impressions || 0, 10)
        total.clicks += parseInt(record.clicks || 0, 10)
        total.orders += parseInt(record.orders || 0, 10)
        total.st_impr_rank += parseFloat(record.st_impr_rank || 0)
        total.st_impr_share += parseFloat(record.st_impr_share || 0)
        total.viewable_impressions += parseInt(record.viewable_impressions || 0, 10)

        children.push({
          ...calcDerivedMetrics(record),
          campaignName: campaignDetail.name || '',
          campaignType,
          targetingType: campaignDetail.targetingType || '',
          costType: campaignDetail.costType || '',
          matchType: capitalizeFirstLetter(record.match_type),
        })
      })

      if (children.length) {
        total.st_impr_rank /= children.length
        total.st_impr_share /= children.length

        extendedSearchTerms.push({
          search: searchTerm,
          ...calcDerivedMetrics(total),
          children,
        })
      }
    })

    setSearchTerms(extendedSearchTerms)
  }, [findDupsData, campaignsWithHistory, filterValues])

  const [columns, columnSelection] = useMemo(() => {
    const availableColumns = campaignTableColumns.filter(c1 =>
      bulkSTColumnList.find(c2 => c2.key === c1.key)
    )
    return [
      [...initialColumns, ...availableColumns],
      availableColumns,
    ]
  }, [campaignTableColumns])

  const renderRecord = record => (
    <>
      <div className="table-col col-search-term" title={record.search}>
        <strong className="contents">
          { record.search }
        </strong>
      </div>
      {
        columnSelection.map(column => (
          <TableCell
            key={column.key}
            record={record}
            columnKey={column.key}
            columnSelection={columnSelection}
            currencySign={currencySign}
            currencyRate={currencyRate}
          />
        ))
      }
    </>
  )

  // Render aggregation row.
  const renderTotal = summary => (
    <>
      <div className="table-col col-search-term">Totals:</div>
      {
        columnSelection.map(column => (
          <TableCell
            key={column.key}
            record={summary}
            columnKey={column.key}
            columnSelection={columnSelection}
            currencySign={currencySign}
            currencyRate={currencyRate}
          />
        ))
      }
    </>
  )

  const getExportData = (exportableColumns, record) => (
    exportableColumns.map((column) => {
      if (column.key === 'search') {
        return record.search
      }
      if (column.key === 'keyword') {
        return record.keyword
      }
      if (column.key === 'matchType') {
        return record.matchType
      }
      return getExportValueForColumn(record, column.key, currencySign, currencyRate)
    })
  )

  return (
    <>
      <SortableTable
        columns={columns}
        defaultSort={['cost', 'desc']}
        sorter={tableSorter(['search'])}
        className="table-search-terms"
        records={searchTerms}
        idField="search"
        searchFields={['search']}
        noCheckBox
        hasSticky
        hasDateRange
        hasLifetimeRange
        filterName="dupResult"
        columnEditorId="dupSearchTermResult"
        columnList={bulkSTColumnList}
        columnSelection={columnSelection}
        exportFileName="Find Duplicate Targets"
        getExportData={getExportData}
        renderRecord={renderRecord}
        renderTotal={renderTotal}
        onClickRecord={setSelectedSearchTerm}
        onChangeDate={onChangeDate}
      />
      {
        selectedSearchTerm !== null && (
          <DupSTModal
            duplicatedSearchTerm={selectedSearchTerm}
            onClose={() => { setSelectedSearchTerm(null) }}
          />
        )
      }
    </>
  )
}

export default DupSTTable
